import { useState, useEffect, useRef } from 'react';
import { Menu, X, Instagram, Linkedin, Twitter, Facebook, ChevronDown, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { prefetchHandlers } from '@/lib/prefetch';
// Logo is now in public folder for preloading
const logo = '/images/new-logo.png';

const serviceLinks = [
  { name: 'Local SEO', href: '/services/local-seo' },
  { name: 'YouTube SEO', href: '/services/youtube-seo' },
  { name: 'E-commerce SEO', href: '/services/ecommerce-seo' },
  { name: 'Meta Ads', href: '/services/meta-ads' },
  { name: 'Digital Marketing', href: '/services/digital-marketing' },
];

const Navigation = ({ darkHero = false }: { darkHero?: boolean }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMenuClosing, setIsMenuClosing] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const servicesTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { user } = useAuth();

  // Check admin role
  useEffect(() => {
    const checkAdmin = async () => {
      if (!user) {
        setIsAdmin(false);
        return;
      }
      try {
        const { data } = await supabase.rpc('has_role', {
          _user_id: user.id,
          _role: 'admin'
        });
        setIsAdmin(data === true);
      } catch {
        setIsAdmin(false);
      }
    };
    checkAdmin();
  }, [user]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    // Use passive event listener for better scroll performance
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const sections = ['home', 'about', 'services', 'resume', 'contact'];
    
    const observerOptions = {
      rootMargin: '-100px 0px -66%',
      threshold: 0,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    sections.forEach((sectionId) => {
      const element = document.getElementById(sectionId);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      sections.forEach((sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
          observer.unobserve(element);
        }
      });
    };
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;

  const navLinks = [
    { name: 'Home', href: '/#home', isRoute: false },
    { name: 'About', href: '/#about', isRoute: false },
    { name: 'Services', href: '/#services', isRoute: false },
    { name: 'Portfolio', href: '/portfolio', isRoute: true },
    { name: 'Blog', href: '/blog', isRoute: true },
    { name: 'Contact', href: '/contact', isRoute: true },
  ];

  const socialLinks = [
    { icon: Instagram, href: 'https://www.instagram.com/rakibhossentopu/' },
    { icon: Linkedin, href: 'https://www.linkedin.com/in/rakibhossentopu/' },
    { icon: Twitter, href: 'https://x.com/rakibhassantopu' },
    { icon: Facebook, href: 'https://www.facebook.com/rakibhossentopu' },
  ];

  const closeMobileMenu = () => {
    setIsMenuClosing(true);
    setTimeout(() => {
      setIsMobileMenuOpen(false);
      setIsMenuClosing(false);
    }, 200);
  };

  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, href: string, isRoute: boolean) => {
    if (isRoute) {
      e.preventDefault();
      closeMobileMenu();
      navigate(href);
      return;
    }
    
    e.preventDefault();
    const isHomePage = window.location.pathname === '/';
    const targetId = href.replace('/#', '').replace('#', '');
    
    if (isHomePage) {
      const element = document.getElementById(targetId);
      if (element) {
        const offsetTop = element.offsetTop - 80;
        window.scrollTo({
          top: offsetTop,
          behavior: 'smooth'
        });
        closeMobileMenu();
      }
    } else {
      navigate(href);
    }
  };

  // When on a dark hero page and not scrolled, use white text
  const useLightText = darkHero && !isScrolled;
  const textClass = useLightText ? 'text-white' : 'text-foreground';
  const textMutedClass = useLightText ? 'text-white/60' : 'text-muted-foreground';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-background/95 backdrop-blur-sm shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center group" title="Rakib Hossen Topu - SEO Expert in BD">
            <img src={logo} alt="Rakib Hossen Topu - SEO Expert in BD | Digital Marketing Specialist Bangladesh" width={240} height={64} className="h-10 sm:h-12 md:h-14 lg:h-16 w-auto transition-all duration-300 group-hover:scale-105 group-hover:brightness-110 group-hover:drop-shadow-[0_0_8px_rgba(226,78,33,0.4)]" loading="eager" decoding="async" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => {
              const isHomePage = currentPath === '/';
              const sectionId = link.href.replace('/#', '').replace('/', '');
              const isActive = !link.isRoute && isHomePage && activeSection === sectionId;
              const isBlogActive = link.isRoute && link.href === '/blog' && currentPath.startsWith('/blog');
              const isPortfolioActive = link.isRoute && link.href === '/portfolio' && (currentPath === '/portfolio' || currentPath.startsWith('/projects'));
              const isContactActive = link.isRoute && link.href === '/contact' && currentPath === '/contact';
              const isRouteActive = isBlogActive || isPortfolioActive || isContactActive;
              const isServicesLink = link.name === 'Services';
              const isOnServicePage = currentPath.startsWith('/services');
              
              if (isServicesLink) {
                return (
                  <div 
                    key={link.name}
                    className="relative"
                    onMouseEnter={() => {
                      if (servicesTimeoutRef.current) {
                        clearTimeout(servicesTimeoutRef.current);
                      }
                      setIsServicesOpen(true);
                    }}
                    onMouseLeave={() => {
                      servicesTimeoutRef.current = setTimeout(() => {
                        setIsServicesOpen(false);
                      }, 150);
                    }}
                  >
                    <button
                      className={`transition-colors duration-300 font-medium relative flex items-center gap-1 ${
                        isActive || isOnServicePage
                          ? 'text-primary' 
                          : `${textClass} hover:text-primary`
                      }`}
                    >
                      {link.name}
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''}`} />
                      {(isActive || isOnServicePage) && (
                        <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-primary rounded-full" />
                      )}
                    </button>
                    
                    {/* Hover Dropdown */}
                    <div 
                      className={`absolute top-full left-1/2 -translate-x-1/2 pt-2 transition-all duration-200 ${
                        isServicesOpen 
                          ? 'opacity-100 visible translate-y-0' 
                          : 'opacity-0 invisible -translate-y-2'
                      }`}
                    >
                      <div className="bg-background border border-border rounded-lg shadow-xl min-w-[200px] py-2 overflow-hidden">
                        <a
                          href={link.href}
                          onClick={(e) => {
                            handleSmoothScroll(e, link.href, false);
                            setIsServicesOpen(false);
                          }}
                          className="block px-4 py-2.5 text-sm font-medium text-foreground hover:bg-primary/10 hover:text-primary transition-colors"
                        >
                          All Services
                        </a>
                        <div className="h-px bg-border my-1" />
                        {serviceLinks.map((service) => (
                          <Link 
                            key={service.name}
                            to={service.href}
                            onClick={() => setIsServicesOpen(false)}
                            {...prefetchHandlers(service.href)}
                            className={`block px-4 py-2.5 text-sm transition-colors ${
                              currentPath === service.href
                                ? 'text-primary bg-primary/10 font-medium'
                                : 'text-muted-foreground hover:bg-primary/10 hover:text-primary'
                            }`}
                          >
                            {service.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }
              
              return (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => handleSmoothScroll(e, link.href, link.isRoute)}
                  {...(link.isRoute ? prefetchHandlers(link.href) : {})}
                  className={`transition-colors duration-300 font-medium relative ${
                    isActive || isRouteActive
                      ? 'text-primary' 
                      : `${textClass} hover:text-primary`
                  }`}
                >
                  {link.name}
                  {(isActive || isRouteActive) && (
                    <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-primary rounded-full" />
                  )}
                </a>
              );
            })}
          </div>

          {/* Social Links & Admin */}
          <div className="hidden md:flex items-center space-x-4">
            {isAdmin && (
              <Link
                to="/admin"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-300 ${
                  currentPath === '/admin'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-primary/10 text-primary hover:bg-primary/20'
                }`}
              >
                <Shield className="w-4 h-4" />
                Admin
              </Link>
            )}
            {socialLinks.map((social, index) => (
              <a
                key={index}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className={`${textMutedClass} hover:text-primary transition-colors duration-300`}
              >
                <social.icon className="w-5 h-5" />
              </a>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => isMobileMenuOpen ? closeMobileMenu() : setIsMobileMenuOpen(true)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMobileMenuOpen ? <X className={`w-6 h-6 ${useLightText ? 'text-white' : ''}`} /> : <Menu className={`w-6 h-6 ${useLightText ? 'text-white' : ''}`} />}
          </Button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <>
            {/* Tap-to-close overlay */}
            <div 
              className={`md:hidden fixed inset-0 top-[72px] z-40 bg-black/20 backdrop-blur-sm transition-opacity duration-200 ${
                isMenuClosing ? 'opacity-0' : 'opacity-100'
              }`}
              onClick={closeMobileMenu}
            />
            {/* Menu content */}
            <div className={`md:hidden fixed inset-x-0 top-[72px] bottom-0 z-50 bg-background backdrop-blur-xl shadow-2xl overflow-y-auto transition-all duration-200 ${
              isMenuClosing ? 'opacity-0 translate-y-[-10px]' : 'opacity-100 translate-y-0'
            }`}>
              <div className="flex flex-col p-6 space-y-2">
                {navLinks.map((link) => {
                  const isHomePage = currentPath === '/';
                  const sectionId = link.href.replace('/#', '').replace('/', '');
                  const isActive = !link.isRoute && isHomePage && activeSection === sectionId;
                  const isBlogActive = link.isRoute && link.href === '/blog' && currentPath.startsWith('/blog');
                  const isPortfolioActive = link.isRoute && link.href === '/portfolio' && (currentPath === '/portfolio' || currentPath.startsWith('/projects'));
                  const isContactActive = link.isRoute && link.href === '/contact' && currentPath === '/contact';
                  const isRouteActive = isBlogActive || isPortfolioActive || isContactActive;
                  const isServicesLink = link.name === 'Services';
                  const isServicesActive = currentPath.startsWith('/services');
                  
                  if (isServicesLink) {
                    return (
                      <div key={link.name} className="space-y-1">
                        <a
                          href={link.href}
                          onClick={(e) => handleSmoothScroll(e, link.href, false)}
                          className={`py-4 px-4 rounded-lg transition-all duration-300 font-medium text-lg block ${
                            isActive || isServicesActive
                              ? 'text-primary bg-primary/10' 
                              : 'text-foreground hover:text-primary hover:bg-muted'
                          }`}
                        >
                          {link.name}
                        </a>
                        <div className="ml-4 space-y-1 border-l-2 border-border pl-4">
                          {serviceLinks.map((service) => (
                            <Link
                              key={service.name}
                              to={service.href}
                              onClick={closeMobileMenu}
                              className={`py-2 px-4 rounded-lg transition-all duration-300 font-medium text-base block ${
                                currentPath === service.href
                                  ? 'text-primary bg-primary/10' 
                                  : 'text-muted-foreground hover:text-primary hover:bg-muted'
                              }`}
                            >
                              {service.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  
                  return (
                    <a
                      key={link.name}
                      href={link.href}
                      onClick={(e) => handleSmoothScroll(e, link.href, link.isRoute)}
                      className={`py-4 px-4 rounded-lg transition-all duration-300 font-medium text-lg ${
                        isActive || isRouteActive
                          ? 'text-primary bg-primary/10' 
                          : 'text-foreground hover:text-primary hover:bg-muted'
                      }`}
                    >
                      {link.name}
                    </a>
                  );
                })}
                {isAdmin && (
                  <Link
                    to="/admin"
                    onClick={closeMobileMenu}
                    className={`py-4 px-4 rounded-lg transition-all duration-300 font-medium text-lg flex items-center gap-2 ${
                      currentPath === '/admin'
                        ? 'text-primary bg-primary/10' 
                        : 'text-foreground hover:text-primary hover:bg-muted'
                    }`}
                  >
                    <Shield className="w-5 h-5" />
                    Admin Dashboard
                  </Link>
                )}
                <div className="flex space-x-6 pt-6 mt-4 border-t border-border justify-center">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-primary transition-colors duration-300 p-2"
                    >
                      <social.icon className="w-6 h-6" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
